import React, { useState, useEffect } from 'react';
import { Usuario } from '../../types';
import { useDepartamento } from '../../contexts/DepartamentoContext';
import departamentoService, { LiderancaUsuario } from '../../services/departamentoService';
import { toast } from 'react-toastify';
import Spinner from '../common/Spinner';

interface GerenciamentoLiderancasProps {
  usuario: Usuario;
  onClose: () => void;
  onUpdate: () => void;
}

const GerenciamentoLiderancas: React.FC<GerenciamentoLiderancasProps> = ({ usuario, onClose, onUpdate }) => {
  const { departamentos } = useDepartamento();
  const [liderancas, setLiderancas] = useState<LiderancaUsuario[]>([]);
  const [carregando, setCarregando] = useState<boolean>(true);
  const [departamentoSelecionado, setDepartamentoSelecionado] = useState<string>('');
  const [processando, setProcessando] = useState<boolean>(false);

  // Carregar lideranças do usuário
  useEffect(() => {
    const carregarLiderancas = async () => {
      setCarregando(true);
      try {
        const resposta = await departamentoService.obterLiderancasUsuario(usuario.id);
        if (resposta.success && resposta.liderancas) {
          setLiderancas(resposta.liderancas);
        } else {
          toast.error(resposta.message || 'Erro ao carregar lideranças');
        }
      } catch (error) {
        console.error('Erro ao carregar lideranças:', error);
        toast.error('Erro ao carregar lideranças');
      } finally {
        setCarregando(false);
      }
    };

    carregarLiderancas();
  }, [usuario.id]);

  // Adicionar uma nova liderança
  const adicionarLideranca = async () => {
    if (!departamentoSelecionado) {
      toast.warning('Selecione um departamento');
      return;
    }

    // Verificar se o usuário já é líder deste departamento
    if (liderancas.some(l => l.id === departamentoSelecionado)) {
      toast.warning('O usuário já é líder deste departamento');
      return;
    }

    setProcessando(true);
    try {
      const resposta = await departamentoService.adicionarLider(departamentoSelecionado, usuario.id);
      if (resposta.success) {
        toast.success('Liderança adicionada com sucesso');
        
        // Atualizar a lista de lideranças
        const novaLideranca: LiderancaUsuario = {
          id: departamentoSelecionado,
          nome: departamentos.find(d => d.id === departamentoSelecionado)?.nome || 'Departamento',
          data_atribuicao: new Date().toISOString()
        };
        
        setLiderancas([...liderancas, novaLideranca]);
        setDepartamentoSelecionado('');
        onUpdate(); // Notificar o componente pai sobre a atualização
      } else {
        toast.error(resposta.message || 'Erro ao adicionar liderança');
      }
    } catch (error) {
      console.error('Erro ao adicionar liderança:', error);
      toast.error('Erro ao adicionar liderança');
    } finally {
      setProcessando(false);
    }
  };

  // Remover uma liderança
  const removerLideranca = async (departamentoId: string) => {
    setProcessando(true);
    try {
      const resposta = await departamentoService.removerLider(departamentoId, usuario.id);
      if (resposta.success) {
        toast.success('Liderança removida com sucesso');
        
        // Atualizar a lista de lideranças
        setLiderancas(liderancas.filter(l => l.id !== departamentoId));
        onUpdate(); // Notificar o componente pai sobre a atualização
      } else {
        toast.error(resposta.message || 'Erro ao remover liderança');
      }
    } catch (error) {
      console.error('Erro ao remover liderança:', error);
      toast.error('Erro ao remover liderança');
    } finally {
      setProcessando(false);
    }
  };

  // Departamentos disponíveis (que o usuário ainda não é líder)
  const departamentosDisponiveis = departamentos.filter(
    dep => !liderancas.some(l => l.id === dep.id)
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg">
        <h3 className="text-lg font-semibold mb-4">
          Gerenciar Lideranças - {usuario.nome}
        </h3>
        
        {carregando ? (
          <div className="flex justify-center items-center py-8">
            <Spinner size={32} />
          </div>
        ) : (
          <>
            {/* Lista de lideranças atuais */}
            <div className="mb-6">
              <h4 className="text-md font-medium mb-2 text-gray-700">Departamentos liderados:</h4>
              
              {liderancas.length === 0 ? (
                <p className="text-gray-500 italic">O usuário não é líder de nenhum departamento.</p>
              ) : (
                <ul className="space-y-2">
                  {liderancas.map(lideranca => (
                    <li key={lideranca.id} className="flex justify-between items-center p-2 bg-gray-50 rounded-md">
                      <div>
                        <span className="font-medium">{lideranca.nome}</span>
                        <span className="text-xs text-gray-500 block">
                          Desde: {new Date(lideranca.data_atribuicao).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                      <button
                        onClick={() => removerLideranca(lideranca.id)}
                        disabled={processando}
                        className="text-red-600 hover:text-red-800 disabled:opacity-50"
                        title="Remover liderança"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            
            {/* Adicionar nova liderança */}
            <div className="border-t pt-4">
              <h4 className="text-md font-medium mb-2 text-gray-700">Adicionar nova liderança:</h4>
              
              <div className="flex space-x-2">
                <select
                  value={departamentoSelecionado}
                  onChange={(e) => setDepartamentoSelecionado(e.target.value)}
                  className="flex-1 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-bravo-primary focus:border-bravo-primary"
                  disabled={processando || departamentosDisponiveis.length === 0}
                >
                  <option value="">Selecione um departamento</option>
                  {departamentosDisponiveis.map(departamento => (
                    <option key={departamento.id} value={departamento.id}>
                      {departamento.nome}
                    </option>
                  ))}
                </select>
                
                <button
                  onClick={adicionarLideranca}
                  disabled={processando || !departamentoSelecionado}
                  className="bg-bravo-primary text-white px-4 py-2 rounded-md hover:bg-bravo-primary-dark transition-colors disabled:opacity-50"
                >
                  Adicionar
                </button>
              </div>
              
              {departamentosDisponiveis.length === 0 && (
                <p className="text-yellow-600 text-sm mt-2">
                  O usuário já é líder de todos os departamentos disponíveis.
                </p>
              )}
            </div>
          </>
        )}
        
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default GerenciamentoLiderancas;
